import {
    CHAT_MESSAGE_DIRECTIONS,
    type ChatMessageDirection,
    WHATSAPP_CUSTOMER_SERVICE_WINDOW_MS,
} from 'cpool-constants';

import {
    CHAT_STATUS_LABELS,
    CHAT_WINDOW_STATES,
    type ChatStatusLabel,
    type ChatWindowState,
} from '~/features/whatsapp/constants/chatStatus';

export type { ChatStatusLabel, ChatWindowState };
export { CHAT_STATUS_LABELS, CHAT_WINDOW_STATES };

// Single source of truth for the 24h-window state — all consumers must derive from here.
// `null`/`undefined` is a distinct state from "expired": no inbound yet shows a template nudge,
// not the "Freie Nachricht nicht möglich" callout.
export function deriveWindowState(lastInboundAt: number | null | undefined): ChatWindowState {
    if (lastInboundAt == null) return CHAT_WINDOW_STATES.NO_INBOUND_YET;
    return Date.now() - lastInboundAt <= WHATSAPP_CUSTOMER_SERVICE_WINDOW_MS
        ? CHAT_WINDOW_STATES.OPEN
        : CHAT_WINDOW_STATES.EXPIRED;
}

// Distinct from `ChatWindowState`: reflects who-needs-to-act, not whether free-text is allowed.
// A chat can be simultaneously "Wartet auf Kunden" and "24h aktiv".
export function deriveChatStatusLabel(params: {
    lastInboundAt?: number | null | undefined;
    lastMessageAt: number | null | undefined;
    lastMessageDirection?: ChatMessageDirection | null | undefined;
}): ChatStatusLabel {
    // Started but never used (no message either way) — not "Beantwortet".
    if (params.lastMessageAt == null) return CHAT_STATUS_LABELS.NO_MESSAGES;
    const windowState = deriveWindowState(params.lastInboundAt);
    if (windowState === CHAT_WINDOW_STATES.EXPIRED) return CHAT_STATUS_LABELS.WINDOW_EXPIRED;
    if (params.lastMessageDirection === CHAT_MESSAGE_DIRECTIONS.OUTBOUND) {
        return CHAT_STATUS_LABELS.AWAITING_CUSTOMER_REPLY;
    }
    if (windowState === CHAT_WINDOW_STATES.OPEN) return CHAT_STATUS_LABELS.WINDOW_OPEN;
    return CHAT_STATUS_LABELS.ANSWERED;
}

// Shared color mapping; hoisted from ChatListItem so StatusSection renders the same pill.
export const CHAT_STATUS_PILL_CLASSES: Record<ChatStatusLabel, string> = {
    [CHAT_STATUS_LABELS.ANSWERED]: 'bg-status-blue-bg text-status-blue-text',
    [CHAT_STATUS_LABELS.AWAITING_CUSTOMER_REPLY]: 'bg-status-yellow-bg text-status-yellow-text',
    [CHAT_STATUS_LABELS.NO_MESSAGES]: 'bg-grey-light text-grey',
    [CHAT_STATUS_LABELS.WINDOW_EXPIRED]: 'bg-status-red-bg text-status-red-text',
    [CHAT_STATUS_LABELS.WINDOW_OPEN]: 'bg-status-green-bg text-status-green-text',
};

export const CHAT_UNLINKED_PILL_CLASS = 'bg-grey-light text-midnight';
